import {
  Button,
  CircularProgress,
  Modal,
  Snackbar,
  SnackbarContent,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Box,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import * as Yup from "yup";

import {
  createClientWithEquipment,
  updateCustomer,
  getAllCustomersAndEquipments,
  createCustomer,
} from "src/services/customers";
import { getEmployee } from "src/services/employee";
import StyledTextField from "../StyledTextField";
import {
  ButtonContainer,
  ClientContainter,
  InputContainer,
  ModalContainer,
} from "./styles";

import { formatDocument, formatPhone } from "src/utils/formatters";
import Loading from "../Loading";
import { createDocument, getDocuments, deleteDocument, updateDocument } from "src/services/documents";
import DocumentsTab from "../DocumentTab";
import EquipmentTab from "../EquipmentTab";
import { deleteEquipment } from "src/services/equipments";

export default function NewClientModal({
  open,
  handleClose,
  customer,
  tabMode,
  isAdmin,
}: {
  open: boolean;
  handleClose: () => void;
  customer?: any;
  tabMode?: number;
  isAdmin?: boolean;
}) {
  const [currentCustomer, setCurrentCustomer] = useState(customer || null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(tabMode || 0);
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error";
  } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [searchingDocument, setSearchingDocument] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [editingDocId, setEditingDocId] = useState<number | null>(null);
  const isEditingDoc = Boolean(editingDocId);

  const clientSchema = {
    name: Yup.string()
      .min(3, "Mínimo de 3 caracteres")
      .required("Campo obrigatório"),
    document: Yup.string().required("Campo obrigatório"),
    email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
    contactName: Yup.string().required("Campo obrigatório"),
    phone: Yup.string().required("Campo obrigatório"),
  };

  const equipmentSchema = {
    equipmentName: Yup.string()
      .min(3, "Mínimo de 3 caracteres")
      .required("Campo obrigatório"),
    model: Yup.string()
      .min(3, "Mínimo de 3 caracteres")
      .required("Campo obrigatório"),
    employee_id: Yup.string().required("Campo obrigatório"),
    serialNumber: Yup.string().required("Campo obrigatório"),
    brand: Yup.string()
      .min(3, "Mínimo de 3 caracteres")
      .required("Campo obrigatório"),
    calibrationDate: Yup.date().required("Campo obrigatório").nullable(),
    expiration_date: Yup.date()
      .required("Campo obrigatório")
      .nullable()
      .test(
        "is-after-calibration",
        "O vencimento deve ser posterior à data de calibração",
        function (value) {
          const { calibrationDate } = this.parent;
          if (!value || !calibrationDate) return true;
          return new Date(value) >= new Date(calibrationDate);
        },
      ),
    pdf_file: Yup.mixed<File>()
      .required("PDF é obrigatório")
      .test("fileFormat", "Apenas PDF", (value) =>
        value ? value.type === "application/pdf" : false,
      ),
  };

  const documentSchema = {
    documentName: Yup.string().required("Campo obrigatório"),
    documentStatus: Yup.string().required("Campo obrigatório"),
    documentExpiration: Yup.date().required("Campo obrigatório").nullable(),
    employee: Yup.string().required("Campo obrigatório"),

    documentFile: Yup.mixed<File>()
      .required("PDF obrigatório")
      .test("fileFormat", "Apenas PDF", (value) =>
        value ? value.type === "application/pdf" : false,
      ),
  };

  const validationSchema = Yup.object().shape({
    ...clientSchema,
    ...(tab === 0 ? equipmentSchema : {}),
    ...(tab === 1 ? documentSchema : {}),
  });

  const initialValues = {
    name: currentCustomer?.name || "",
    document: currentCustomer?.document || "",
    email: currentCustomer?.email || "",
    contactName: currentCustomer?.contact_name || "",
    phone: currentCustomer?.phone || "",

    // EQUIPAMENTO
    equipmentName: "",
    model: "",
    serialNumber: "",
    brand: "",
    employee_id: "",
    technician: null,
    calibrationDate: "",
    expiration_date: "",
    pdf_file: null as File | null,

    // DOCUMENTOS
    documentName: "",
    documentStatus: "",
    documentType: "",
    documentExpiration: "",
    documentFile: null as File | null,
    employee: "",
  };

  useEffect(() => {
    if (customer) setCurrentCustomer(customer);
  }, [customer]);

  useEffect(() => {
    const fetchEmployees = async () => {
      if (!open) return;
      try {
        const data = await getEmployee();
        setEmployees(data?.data || []);
      } catch (error) {
        console.error("Erro ao carregar funcionários:", error);
      }
    };
    fetchEmployees();
  }, [open]);

  const handleChangeTab = (_: any, newValue: number) => {
    setTab(newValue);
    setEditingDocId(null);
  };

  const handleUnlock = () => {
    const now = new Date();
    const key = `${String(now.getMinutes()).padStart(2, "0")}${now.getDate() * (now.getMonth() + 1)}`;
    const input = prompt("🔒 Digite a chave de acesso:");
    if (input === key) {
      setUnlocked(true);
      setToast({ message: "Campos desbloqueados ✅", type: "success" });
    } else {
      setToast({ message: "Chave incorreta ❌", type: "error" });
    }
  };

  const handleSave = async (
    values: typeof initialValues,
    { resetForm }: any,
  ) => {
    try {
      setLoading(true);

      if (tab === 1) {
        try {
          let customerId = currentCustomer?.id;

          if (isEditingDoc) {
            const formData = new FormData();

            formData.append("name", values.documentName);
            formData.append("status", values.documentStatus);
            formData.append("type", values.documentType);
            formData.append("expiration_date", values.documentExpiration);
            formData.append("employees[0]", values.employee);

            if (customerId) {
              formData.append("customers[0]", customerId);
            }

            if (values.documentFile) {
              formData.append("pdf_file", values.documentFile);
            }

            formData.append("_method", "PUT");

            await updateDocument(editingDocId as number, formData);
          } else {
            if (!customerId) {
              const customerResponse = await createCustomer({
                name: values.name,
                document: values.document,
                contact_name: values.contactName,
                phone: values.phone,
                email: values.email,
              });

              if (!customerResponse?.data?.id) {
                throw new Error("Erro ao criar cliente");
              }

              customerId = customerResponse.data.id;
              setCurrentCustomer(customerResponse.data);
            }

            const formData = new FormData();

            formData.append("name", values.documentName);
            formData.append("status", values.documentStatus);
            formData.append("type", values.documentType);
            formData.append("expiration_date", values.documentExpiration);
            formData.append("employees[0]", values.employee);
            formData.append("customers[0]", customerId);

            if (values.documentFile) {
              formData.append("pdf_file", values.documentFile);
            }

            await createDocument(formData);
          }

          const documentsResponse = await getDocuments();

          const customerDocuments = (documentsResponse?.data || []).filter(
            (doc: any) => {
              const docCustomerId = doc.customer_id || doc.customers?.[0]?.id;
              return String(docCustomerId) === String(customerId);
            }
          );

          setCurrentCustomer((prev: any) => ({
            ...prev,
            documents: customerDocuments,
          }));

          setToast({
            message: isEditingDoc
              ? "Documento atualizado! ✅"
              : "Documento criado com sucesso! ✅",
            type: "success",
          });

          setEditingDocId(null);

          resetForm();

        } catch (error: any) {
          setToast({
            message:
              error?.response?.data?.message ||
              error?.message ||
              "Erro ao salvar documento ❌",
            type: "error",
          });

          return;
        } finally {
          setLoading(false);
        }

        return;
      }

      const clientData = {
        name: values.name,
        document: values.document,
        contact_name: values.contactName,
        phone: values.phone,
        email: values.email,
        equipment: [
          {
            name: values.equipmentName,
            model: values.model,
            serial_number: values.serialNumber,
            brand: values.brand,
            employee_id: values.employee_id
              ? Number(values.employee_id)
              : null,
            calibration_date: values.calibrationDate,
            expiration_date: values.expiration_date,
            pdf_file:
              values.pdf_file instanceof File ? values.pdf_file : undefined,
          },
        ],
      };

      const response = currentCustomer?.id
        ? await updateCustomer(currentCustomer.id, clientData)
        : await createClientWithEquipment(clientData);

      setToast({ message: "Dados salvos com sucesso! ✅", type: "success" });
      setCurrentCustomer(response?.data);

    } catch (error: any) {
      setToast({
        message: error.response?.data?.message || "Erro ao salvar dados ❌",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (id: number) => {
    if (!id) return;
    try {
      setDeleting(true);
      const response = await deleteDocument(id);
      if (response?.status === 204) {
        setToast({ message: "Documento excluído! ✅", type: "success" });
        setCurrentCustomer((prev: any) => ({
          ...prev,
          documents: prev?.documents?.filter((doc: any) => doc.id !== id) || [],
        }));
        if (editingDocId === id) setEditingDocId(null);
      }
    } catch {
      setToast({ message: "Erro ao excluir documento ❌.", type: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteEquipment = async (id: number) => {
    if (!id) return;

    try {
      setDeleting(true);
      const response = await deleteEquipment(id);
      if (response?.status === 204) {
        setToast({
          message: "Equipamento excluído com sucesso! ✅",
          type: "success",
        });

        setCurrentCustomer((prev: any) => ({
          ...prev,
          equipment: prev?.equipment?.filter((eq: any) => eq.id !== id) || [],
        }));
      } else {
        setToast({ message: "Erro ao excluir equipamento ❌.", type: "error" });
      }
    } catch {
      setToast({ message: "Erro ao excluir equipamento ❌.", type: "error" });
    } finally {
      setDeleting(false);
    }
  };
  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <ModalContainer>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSave}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              setFieldValue,
              handleSubmit,
              resetForm,
            }) => (
              <form onSubmit={handleSubmit}>
                <ClientContainter>
                  <Typography
                    component="h2"
                    gutterBottom
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    Dados do Cliente
                    <IconButton
                      size="small"
                      onClick={handleUnlock}
                      sx={{
                        opacity: 0,
                        width: 22,
                        height: 22,
                        ml: 1,
                        "&:hover": { opacity: 0.1, border: "1px solid #000" },
                      }}
                    >
                      <LockOpenIcon fontSize="small" />
                    </IconButton>
                  </Typography>

                  <InputContainer>
                    <StyledTextField
                      label="CNPJ/CPF"
                      name="document"
                      value={values.document}
                      onChange={(e) => {
                        setFieldValue(
                          "document",
                          formatDocument(e.target.value),
                        );
                        if (currentCustomer) setCurrentCustomer(null);
                      }}
                      onBlur={async (e) => {
                        handleBlur(e);
                        const doc = e.target.value.replace(/\D/g, "");
                        if (!doc) return;
                        setSearchingDocument(true);
                        setLoading(true);
                        try {
                          const response = await getAllCustomersAndEquipments();
                          const found = (response?.data?.data || []).find(
                            (c: any) => c.document.replace(/\D/g, "") === doc,
                          );
                          if (found) {
                            setCurrentCustomer(found);
                            setFieldValue("name", found.name);
                            setFieldValue("contactName", found.contact_name);
                            setFieldValue("email", found.email);
                            setFieldValue("phone", formatPhone(found.phone));
                            setToast({
                              message: "Cliente encontrado ✅",
                              type: "success",
                            });
                          }
                        } finally {
                          setSearchingDocument(false);
                          setLoading(false);
                        }
                      }}
                      error={touched.document && Boolean(errors.document)}
                      helperText={
                        touched.document && (errors.document as string)
                      }
                      fullWidth
                      disabled={
                        searchingDocument ||
                        (!!currentCustomer?.id && !unlocked)
                      }
                    />
                    <StyledTextField
                      label="Cliente"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && (errors.name as string)}
                      fullWidth
                      disabled={
                        searchingDocument ||
                        (!!currentCustomer?.id && !unlocked)
                      }
                    />
                  </InputContainer>

                  <InputContainer>
                    <StyledTextField
                      label="Nome do Contato"
                      name="contactName"
                      value={values.contactName}
                      onChange={handleChange}
                      error={touched.contactName && Boolean(errors.contactName)}
                      helperText={
                        touched.contactName && (errors.contactName as string)
                      }
                      disabled={searchingDocument}
                    />
                    <StyledTextField
                      label="Telefone"
                      name="phone"
                      value={values.phone}
                      onChange={(e) =>
                        setFieldValue("phone", formatPhone(e.target.value))
                      }
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && (errors.phone as string)}
                      disabled={searchingDocument}
                    />
                    <StyledTextField
                      label="Email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && (errors.email as string)}
                      disabled={searchingDocument}
                    />
                  </InputContainer>
                </ClientContainter>

                <Box sx={{ borderBottom: 1, borderColor: "divider", mt: 2 }}>
                  <Tabs value={tab} onChange={handleChangeTab}>
                    <Tab
                      label={`Equipamentos (${currentCustomer?.equipment?.length || 0})`}
                    />
                    {isAdmin && (<Tab label={`Documentos (${currentCustomer?.documents?.length || 0})`} />)}
                  </Tabs>
                </Box>

                {tab === 0 && (
                  <EquipmentTab
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    employees={employees}
                    currentCustomer={currentCustomer}
                    onDeleteEquipment={handleDeleteEquipment}
                    disabled={searchingDocument}
                  />
                )}

                {tab === 1 && (
                  <DocumentsTab
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    employees={employees}
                    disabled={searchingDocument}
                    currentCustomer={currentCustomer}
                    onDeleteDocument={handleDeleteDocument}
                    isEditing={isEditingDoc}
                    setEditingDocId={setEditingDocId}
                    onCancelEdit={() => {
                      setEditingDocId(null);
                      resetForm();
                    }}
                  />
                )}

                <ButtonContainer>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setCurrentCustomer(null);
                      resetForm();
                      handleClose();
                    }}
                    disabled={loading}
                  >
                    Fechar
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading || searchingDocument}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Salvar"
                    )}
                  </Button>
                </ButtonContainer>
              </form>
            )}
          </Formik>
          {deleting && <Loading />}
        </ModalContainer>
      </Modal>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <SnackbarContent
          message={toast?.message}
          sx={{
            backgroundColor: "#fff",
            color: toast?.type === "success" ? "#4caf50" : "#f44336",
            fontWeight: 700,
            borderRadius: 1,
          }}
        />
      </Snackbar>
    </>
  );
}
